import {switchMap} from "rxjs/operators";
import {ofType} from "redux-observable";

import * as actionTypes from "../constants/actionTypes";
import * as config from "../constants/config";
import * as handlers from "../constants/handlers";
import * as methods from "../constants/methods";
import * as statsTypes from "../constants/statsTypes";
import * as dispensingValidationActions from "../actions/dispensingValidationActions";
import * as statisticsTabActions from "../actions/statisticsTabActions";
import * as filteringHelper from "../helpers/filteringHelper";
import * as modelHelper from "../helpers/modelHelper";

const statisticsTabInit = (action$, state$) =>
    action$.pipe(
        ofType(actionTypes.STATISTICS_TAB_INIT),
        switchMap((action) => {
            return [
                statisticsTabActions.getStatisticsRequest(
                    action.payload.tabId,
                    filteringHelper.getFilteringParams(action.payload.tabId, state$.value),
                    null,
                    state$.value.filtering[action.payload.tabId].sortingCriterion,
                    false,
                    0,
                    !state$.value.statisticsTab.showHidden
                )
            ];
        })
    );

const addToStatistics = (action$, state$) =>
    action$.pipe(
        ofType(actionTypes.ADD_DISPENSING_FORMULA_TO_STATS),
        switchMap(({payload}) => {
            let formulaStore = state$.value.formula[payload.tabId];

            if (
                formulaStore.product === null ||
                formulaStore.fandeck === null ||
                formulaStore.color === null ||
                formulaStore.base === null ||
                formulaStore.formula === null ||
                formulaStore.package === null
            ) {
                return [];
            }

            if (formulaStore.formula.isCorrection()) {
                if (formulaStore.statisticsId === null) {
                    return [];
                }

                let colorStatsType = formulaStore.formula.getStatsType();
                let spectralData = null;

                if (
                    state$.value.matchingTab[payload.tabId] !== undefined &&
                    (state$.value.matchingTab[payload.tabId].targetCif === null ||
                        state$.value.matchingTab[payload.tabId].targetCif.id !== 0)
                ) {
                    colorStatsType = statsTypes.MODIFIED_AUTOMATIC_CORRECTION;

                    if (state$.value.matchingTab[payload.tabId].targetMeasurementResult !== null) {
                        spectralData = state$.value.matchingTab[payload.tabId].targetMeasurementResult.spectral;
                    }
                }

                return [
                    {
                        type: actionTypes.SERVER_REQUEST,
                        payload: {
                            type: methods.METHOD_ADD_COLOR_STATS_CORRECTION,
                            params: {
                                colorStatsId: formulaStore.statisticsId,
                                formulaColorants: formulaStore.formula.getCorrectionAmounts(),
                                colorStatsType: colorStatsType,
                                rgb: formulaStore.formula.color.rgb,
                                moreInfo: formulaStore.dispenseNote,
                                moreInfoInternal: formulaStore.moreInfoInternal,
                                clientName: state$.value.module.clientIdentifier,
                                priceList: modelHelper.formatPricesForStats(formulaStore.priceList),
                                infoData: formulaStore.infoData,
                                printData: formulaStore.printData,
                                tmcData: formulaStore.tmcData,
                                reflData: spectralData
                            }
                        },
                        method: handlers.ADD_COLOR_STATS_CORRECTION,
                        tabId: payload.tabId
                    }
                ];
            }

            if (formulaStore.numCanDone >= formulaStore.numCanTotal) {
                return [];
            }

            if (formulaStore.numCanDone > 0 && formulaStore.statisticsId !== null) {
                return [
                    {
                        type: actionTypes.MACHINE_DISPENSE_FORMULA,
                        payload: {
                            colorStatsId: formulaStore.statisticsId,
                            tabId: payload.tabId
                        }
                    }
                ];
            }

            let measurementSpectralData = null;

            if (
                state$.value.matchingTab[payload.tabId] !== undefined &&
                state$.value.matchingTab[payload.tabId].measurementResult !== null
            ) {
                measurementSpectralData = state$.value.matchingTab[payload.tabId].measurementResult.spectral;
            }

            return [
                {
                    type: actionTypes.SERVER_REQUEST,
                    payload: {
                        type: methods.METHOD_ADD_COLOR_STATS,
                        params: {
                            brand: state$.value.server.activeDatabase.name,
                            productId: formulaStore.product.id,
                            fandeckId: formulaStore.fandeck.id === config.UNKNOWN_ID ? null : formulaStore.fandeck.id,
                            fandeckLocal: formulaStore.color.local,
                            colorId: formulaStore.color.id === config.UNKNOWN_ID ? null : formulaStore.color.id,
                            colorLocal: formulaStore.color.local,
                            formulaId: formulaStore.formula.id === config.UNKNOWN_ID ? null : formulaStore.formula.id,
                            formulaLocal: formulaStore.color.local,
                            baseId: formulaStore.base.id,
                            formulaColorants: formulaStore.formula.getRealColorantAmounts(),
                            originalFormulaColorants: formulaStore.formula.getColorantAmounts(),
                            customerId: formulaStore.customer !== null ? formulaStore.customer.id : null,
                            packageCount: formulaStore.numCanTotal,
                            customPackage: formulaStore.package.package.isCustom(),
                            packageId: formulaStore.package.package.isCustom() ? null : formulaStore.package.id,
                            customPackageSize: formulaStore.package.package.isCustom()
                                ? formulaStore.package.package.nominalAmount
                                : null,
                            customPackageUnit: formulaStore.package.package.isCustom()
                                ? formulaStore.package.package.unit
                                : null,
                            marginId: formulaStore.margin !== null ? formulaStore.margin.id : null,
                            marginLocal: formulaStore.margin !== null ? formulaStore.margin.local : false,
                            moreInfo: formulaStore.dispenseNote,
                            moreInfoInternal: formulaStore.moreInfoInternal,
                            clientName: state$.value.module.clientIdentifier,
                            scaling: formulaStore.formula.scaling,
                            colorStatsType: formulaStore.formula.getStatsType(),
                            colorName: formulaStore.formula.getAutomaticName(state$.value.settings.formulaUnits),
                            colorRgb: measurementSpectralData === null ? null : formulaStore.formula.color.rgb,
                            fandeckName: formulaStore.fandeck.displayName,
                            priceList: modelHelper.formatPricesForStats(formulaStore.priceList),
                            infoData: formulaStore.infoData,
                            printData: formulaStore.printData,
                            tmcData: formulaStore.tmcData,
                            reflData: measurementSpectralData,
                            buildingSite: formulaStore.buildingSite
                        }
                    },
                    method: handlers.ADD_COLOR_STATS,
                    tabId: payload.tabId
                }
            ];
        })
    );

const saveFormulaDirectlyToStats = (action$, state$) =>
    action$.pipe(
        ofType(actionTypes.SAVE_FORMULA_DIRECTLY_TO_STATISTICS),
        switchMap(({payload}) => {
            let formulaStore = state$.value.formula[payload.tabId];

            if (formulaStore.formula.getStatsType() === statsTypes.QUICK_TINT) {
                return [
                    {
                        type: actionTypes.SERVER_REQUEST,
                        payload: {
                            type: methods.METHOD_ADD_QUICK_TINT_INTO_STATISTICS,
                            params: {
                                colorants: formulaStore.formula.getRealColorantAmounts(),
                                clientName: state$.value.module.clientIdentifier,
                                priceList: modelHelper.formatPricesForStats(formulaStore.priceList),
                                infoData: formulaStore.infoData,
                                printData: formulaStore.printData,
                                tmcData: formulaStore.tmcData,
                                moreInfo: formulaStore.dispenseNote,
                                moreInfoInternal: formulaStore.moreInfoInternal,
                                colorRgb: state$.value.settings.generateColorPreview
                                    ? formulaStore.formula.color.rgb
                                    : null
                            }
                        },
                        method: handlers.ADD_QUICK_TINT_STATS_DIRECTLY,
                        tabId: payload.tabId
                    }
                ];
            }

            if (formulaStore.formula.isCorrection()) {
                if (formulaStore.statisticsId === null) {
                    return [];
                }

                let colorStatsType = formulaStore.formula.getStatsType();
                let spectralData = null;

                if (
                    state$.value.matchingTab[payload.tabId] !== undefined &&
                    (state$.value.matchingTab[payload.tabId].targetCif === null ||
                        state$.value.matchingTab[payload.tabId].targetCif.id !== 0)
                ) {
                    colorStatsType = statsTypes.MODIFIED_AUTOMATIC_CORRECTION;
                    spectralData = state$.value.matchingTab[payload.tabId].targetMeasurementResult.spectral;
                }

                return [
                    {
                        type: actionTypes.SERVER_REQUEST,
                        payload: {
                            type: methods.METHOD_ADD_COLOR_STATS_CORRECTION,
                            params: {
                                colorStatsId: formulaStore.statisticsId,
                                formulaColorants: formulaStore.formula.getCorrectionAmounts(),
                                colorStatsType: colorStatsType,
                                rgb: formulaStore.formula.color.rgb,
                                moreInfo: formulaStore.dispenseNote,
                                moreInfoInternal: formulaStore.moreInfoInternal,
                                clientName: state$.value.module.clientIdentifier,
                                priceList: modelHelper.formatPricesForStats(formulaStore.priceList),
                                infoData: formulaStore.infoData,
                                printData: formulaStore.printData,
                                tmcData: formulaStore.tmcData,
                                reflData: spectralData
                            }
                        },
                        method: handlers.ADD_COLOR_STATS_DIRECTLY,
                        tabId: payload.tabId
                    }
                ];
            }

            return [
                {
                    type: actionTypes.SERVER_REQUEST,
                    payload: {
                        type: methods.METHOD_ADD_COLOR_STATS,
                        params: {
                            brand: state$.value.server.activeDatabase.name,
                            productId: formulaStore.product.id,
                            fandeckId: formulaStore.fandeck.id === config.UNKNOWN_ID ? null : formulaStore.fandeck.id,
                            fandeckLocal: formulaStore.color.local,
                            colorId: formulaStore.color.id === config.UNKNOWN_ID ? null : formulaStore.color.id,
                            colorLocal: formulaStore.color.local,
                            formulaId: formulaStore.formula.id,
                            formulaLocal: formulaStore.color.local,
                            baseId: formulaStore.base.id,
                            formulaColorants: formulaStore.formula.getRealColorantAmounts(),
                            originalFormulaColorants: formulaStore.formula.getColorantAmounts(),
                            customerId: formulaStore.customer !== null ? formulaStore.customer.id : null,
                            packageCount: formulaStore.numCanTotal,
                            customPackage: formulaStore.package.package.isCustom(),
                            packageId: formulaStore.package.package.isCustom() ? null : formulaStore.package.id,
                            customPackageSize: formulaStore.package.package.isCustom()
                                ? formulaStore.package.package.nominalAmount
                                : null,
                            customPackageUnit: formulaStore.package.package.isCustom()
                                ? formulaStore.package.package.unit
                                : null,
                            marginId: formulaStore.margin !== null ? formulaStore.margin.id : null,
                            marginLocal: formulaStore.margin !== null ? formulaStore.margin.local : false,
                            moreInfo: formulaStore.dispenseNote,
                            moreInfoInternal: formulaStore.moreInfoInternal,
                            clientName: state$.value.module.clientIdentifier,
                            scaling: formulaStore.formula.scaling,
                            colorStatsType: formulaStore.formula.getStatsType(),
                            colorName: formulaStore.formula.getAutomaticName(formulaStore.package.unit),
                            fandeckName: formulaStore.fandeck.displayName,
                            priceList: modelHelper.formatPricesForStats(formulaStore.priceList),
                            infoData: formulaStore.infoData,
                            printData: formulaStore.printData,
                            tmcData: formulaStore.tmcData,
                            buildingSite: formulaStore.buildingSite
                        }
                    },
                    method: handlers.ADD_COLOR_STATS_DIRECTLY,
                    tabId: payload.tabId
                }
            ];
        })
    );

const loadFormulaDataFromStatistics = (action$) =>
    action$.pipe(
        ofType(actionTypes.LOAD_FORMULA_DATA_FROM_STATS),
        switchMap(({payload}) => {
            return [
                {
                    type: actionTypes.SERVER_REQUEST,
                    payload: {
                        type: methods.METHOD_GET_STATISTICS_DATA,
                        params: {
                            colorStatsId: payload.colorStatsId
                        }
                    },
                    method: handlers.GET_FORMULA_DATA_FROM_STATS,
                    tabId: payload.tabId
                }
            ];
        })
    );

const loadFormulaMetaDataFromStatistics = (action$) =>
    action$.pipe(
        ofType(actionTypes.LOAD_FORMULA_META_DATA_FROM_STATS),
        switchMap(({payload}) => {
            return [
                {
                    type: actionTypes.SERVER_REQUEST,
                    payload: {
                        type: methods.METHOD_GET_STATISTICS_DETAIL,
                        params: {
                            colorStatsId: payload.colorStatsId
                        }
                    },
                    method: handlers.GET_FORMULA_META_DATA_FROM_STATS,
                    tabId: payload.tabId,
                    next: payload.next
                }
            ];
        })
    );

const updateStatisticsList = (action$, state$) =>
    action$.pipe(
        ofType(
            actionTypes.STATISTICS_TAB_SAVE_SORTING,
            actionTypes.STATISTICS_SAVE_FILTER,
            actionTypes.STATISTICS_SET_SHOW_HIDDEN,
            actionTypes.STATISTICS_LIST_UPDATE
        ),
        switchMap(({payload}) => {
            return [
                statisticsTabActions.getStatisticsRequest(
                    payload.tabId,
                    filteringHelper.getFilteringParams(payload.tabId, state$.value),
                    state$.value.statisticsTab.filter,
                    state$.value.statisticsTab.sortingCriterion,
                    state$.value.statisticsTab.sortingAsc,
                    0,
                    !state$.value.statisticsTab.showHidden
                )
            ];
        })
    );

const getStatisticsListWithOffset = (action$, state$) =>
    action$.pipe(
        ofType(actionTypes.STATISTICS_TAB_GET_FORMULA_LIST_WITH_OFFSET),
        switchMap(({payload}) => {
            return [
                statisticsTabActions.getStatisticsRequest(
                    payload.tabId,
                    filteringHelper.getFilteringParams(payload.tabId, state$.value),
                    state$.value.statisticsTab.filter,
                    state$.value.statisticsTab.sortingCriterion,
                    state$.value.statisticsTab.sortingAsc,
                    state$.value.statisticsTab.offset,
                    !state$.value.statisticsTab.showHidden,
                    handlers.GET_STATISTICS_WITH_OFFSET
                )
            ];
        })
    );

const startRetint = (action$, state$) =>
    action$.pipe(
        ofType(actionTypes.STATISTICS_TAB_START_RETINT),
        switchMap(({payload}) => {
            let formulaStore = state$.value.formula[payload.tabId];

            if (formulaStore === undefined || formulaStore.formula === null) {
                return [];
            }

            return [
                dispensingValidationActions.getValidationActionsSequence(
                    payload.tabId,
                    statisticsTabActions.addColorStatsToStatsAndDispense,
                    formulaStore.validation,
                    state$.value.settings.dispensingWithErrorsEnabled,
                    formulaStore.formula.realColorantList,
                    payload.packageCount
                )
            ];
        })
    );

const addColorStatsFromStats = (action$, state$) =>
    action$.pipe(
        ofType(actionTypes.ADD_COLOR_STATS_FROM_STATS_AND_DISPENSE),
        switchMap(({payload}) => {
            let formulaStore = state$.value.formula[payload.tabId];

            if (formulaStore === undefined || formulaStore.statisticsId === null) {
                return [];
            }

            return [
                statisticsTabActions.addHistoryColorToStatistics(
                    payload.tabId,
                    formulaStore.statisticsId,
                    payload.packageCount,
                    formulaStore.customer?.id ?? null,
                    formulaStore.dispenseNote
                )
            ];
        })
    );

const setNumberOfPackages = (action$, state$) =>
    action$.pipe(
        ofType(actionTypes.STATISTICS_TAB_SET_NUMBER_OF_PACKAGES),
        switchMap(({payload}) => {
            let formulaStore = state$.value.formula[payload.tabId];

            if (formulaStore === undefined || formulaStore.formula === null) {
                return [];
            }

            return [
                {
                    type: actionTypes.RELOAD_FORMULA_META_DATA,
                    payload: {
                        tabId: payload.tabId
                    }
                },
                {
                    type: actionTypes.MACHINE_VALIDATE_DISPENSED_FORMULA,
                    payload: {
                        tabId: payload.tabId,
                        formula: formulaStore.formula.getColorantVolumes(),
                        numCanDone: formulaStore.numCanDone,
                        numCanTotal: payload.numberOfPackages,
                        customCan: formulaStore.package?.isCustom() ?? false
                    }
                }
            ];
        })
    );

export default [
    statisticsTabInit,
    addToStatistics,
    loadFormulaDataFromStatistics,
    loadFormulaMetaDataFromStatistics,
    updateStatisticsList,
    getStatisticsListWithOffset,
    saveFormulaDirectlyToStats,
    startRetint,
    addColorStatsFromStats,
    setNumberOfPackages
];
